/**
 * Template variables — the org's merge-field registry (BE #1220).
 *
 * Lists the platform's system variables (read-only, badged "System"), this
 * org's own variables and its custom fields. Org admins create, edit,
 * deactivate and delete the org's own variables; everyone else sees a
 * read-only view. Each org variable has a change history.
 *
 * The form adapts to source_kind (fixed value / member field / program field /
 * custom field / entered by staff) and value_type, and shows the API's
 * validation errors next to the field they are about.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Braces, History, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/contexts/RoleContext';
import { PageContainer, PageHeader } from '@/components/weavein';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  APPLIES_TO,
  FALLBACKS,
  KEY_PATTERN,
  MEMBER_FIELDS,
  PROGRAM_FIELDS,
  SOURCE_KINDS,
  VALUE_TYPES,
  invalidateVariableRegistry,
  parseApiError,
  templateVariablesApi,
  type AppliesTo,
  type Fallback,
  type HistoryEntry,
  type SourceConfig,
  type SourceKind,
  type TemplateVariable,
  type TemplateVariableApiError,
  type ValueType,
  type VariableWriteBody,
} from '@/components/messaging/templateVariables';
import {
  appliesToLabel,
  categoryLabel,
  fallbackLabel,
  memberFieldLabel,
  programFieldLabel,
  scopeLabel,
  sortCategories,
  sourceKindLabel,
  valueTypeLabel,
} from '@/components/messaging/templateVariables/labels';

const ADMIN_ROLES = ['super_admin', 'org_admin'];

const inputClass =
  'w-full rounded-md border border-v2border bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-v2accent disabled:opacity-60';

type EditableKind = Exclude<SourceKind, 'system'>;

interface FormState {
  key: string;
  label: string;
  description: string;
  category: string;
  value_type: ValueType;
  source_kind: EditableKind;
  static_value: string;
  field: string;
  field_name: string;
  prompt: string;
  placeholder: string;
  options: string;
  default_value: string;
  fallback: Fallback;
  applies_to: AppliesTo;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  key: '',
  label: '',
  description: '',
  category: 'custom',
  value_type: 'text',
  source_kind: 'static',
  static_value: '',
  field: '',
  field_name: '',
  prompt: '',
  placeholder: '',
  options: '',
  default_value: '',
  fallback: 'block',
  applies_to: 'both',
  active: true,
};

function toForm(v: TemplateVariable): FormState {
  const c = v.source_config ?? {};
  return {
    key: v.key,
    label: v.label ?? '',
    description: v.description ?? '',
    category: v.category || 'custom',
    value_type: v.value_type,
    source_kind: (v.source_kind === 'system' ? 'static' : v.source_kind) as EditableKind,
    static_value: c.value ?? '',
    field: c.field ?? '',
    field_name: c.field_name ?? '',
    prompt: c.prompt ?? '',
    placeholder: c.placeholder ?? '',
    options: (c.options ?? []).join('\n'),
    default_value: v.default_value ?? '',
    fallback: v.fallback,
    applies_to: v.applies_to,
    active: v.active,
  };
}

function parseOptions(raw: string): string[] {
  return raw
    .split(/\n|,/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildSourceConfig(f: FormState): SourceConfig {
  switch (f.source_kind) {
    case 'static':
      return { value: f.static_value };
    case 'member_field':
    case 'program_field':
      return { field: f.field };
    case 'custom_field':
      return { field_name: f.field_name };
    case 'input': {
      const out: SourceConfig = {};
      if (f.prompt.trim()) out.prompt = f.prompt.trim();
      if (f.placeholder.trim()) out.placeholder = f.placeholder.trim();
      if (f.value_type === 'select') out.options = parseOptions(f.options);
      return out;
    }
    default:
      return {};
  }
}

function buildBody(f: FormState, isNew: boolean): VariableWriteBody {
  const body: VariableWriteBody = {
    label: f.label.trim(),
    description: f.description.trim() || null,
    category: f.category.trim() || 'custom',
    value_type: f.value_type,
    source_kind: f.source_kind,
    source_config: buildSourceConfig(f),
    default_value: f.fallback === 'default' ? f.default_value : f.default_value.trim() || null,
    fallback: f.fallback,
    applies_to: f.applies_to,
    active: f.active,
  };
  if (isNew) body.key = f.key.trim();
  return body;
}

/** Which form field an API error code is about. */
function errorField(e: TemplateVariableApiError): string {
  if (e.field) return e.field;
  switch (e.code) {
    case 'invalid_key':
    case 'shadows_system_variable':
    case 'duplicate_key':
      return 'key';
    case 'invalid_label':
      return 'label';
    case 'invalid_value_type':
      return 'value_type';
    case 'invalid_source_kind':
    case 'system_source_not_allowed':
      return 'source_kind';
    case 'invalid_source_config':
      return 'source_config';
    case 'invalid_fallback':
      return 'fallback';
    case 'default_value_required':
      return 'default_value';
    case 'invalid_applies_to':
      return 'applies_to';
    default:
      return 'form';
  }
}

export default function TemplateVariablesManager() {
  const { t } = useAppTranslation();
  const { user } = useAuth();
  const { currentRole } = useRole();
  const roleCanEdit =
    ADMIN_ROLES.includes(String((user as { role?: string } | null)?.role ?? '')) ||
    ADMIN_ROLES.includes(String(currentRole ?? ''));
  const [forbidden, setForbidden] = useState(false);
  const canEdit = roleCanEdit && !forbidden;

  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [appliesFilter, setAppliesFilter] = useState<'all' | AppliesTo>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState<'all' | TemplateVariable['scope']>('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<TemplateVariable | 'new' | null>(null);
  const [historyFor, setHistoryFor] = useState<TemplateVariable | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await templateVariablesApi.list({
        include_inactive: true,
        ...(appliesFilter === 'all' ? {} : { applies_to: appliesFilter }),
      });
      setVariables(data.variables ?? []);
    } catch (err) {
      // '' = no server message; the render shows the translated fallback.
      setLoadError(parseApiError(err).message || '');
    } finally {
      setLoading(false);
    }
  }, [appliesFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const categories = useMemo(() => sortCategories([...new Set(variables.map((v) => v.category || 'general'))]), [variables]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return variables.filter((v) => {
      if (categoryFilter !== 'all' && (v.category || 'general') !== categoryFilter) return false;
      if (scopeFilter !== 'all' && v.scope !== scopeFilter) return false;
      if (!q) return true;
      return v.key.includes(q) || (v.label || '').toLowerCase().includes(q) || (v.description || '').toLowerCase().includes(q);
    });
  }, [variables, categoryFilter, scopeFilter, search]);

  const customFieldOptions = useMemo(
    () => variables.filter((v) => v.scope === 'custom_field').map((v) => ({ value: v.key, label: v.label || v.key })),
    [variables],
  );

  const afterWrite = async () => {
    invalidateVariableRegistry();
    await load();
  };

  const handleWriteError = (err: unknown) => {
    const e = parseApiError(err);
    if (e.status === 403) {
      setForbidden(true);
      setActionError(t('templateVariables.admin.forbidden', 'Only organization admins can change template variables.'));
    } else {
      setActionError(e.message || t('templateVariables.admin.actionFailed', 'That change could not be saved.'));
    }
  };

  const toggleActive = async (v: TemplateVariable) => {
    if (v.id == null) return;
    if (
      v.active &&
      !window.confirm(
        t(
          'templateVariables.admin.confirmDeactivate',
          'Turn off this variable? Templates that use it will report it as turned off, and sends that need it will be blocked. The key stays reserved.',
        ),
      )
    )
      return;
    setActionError(null);
    try {
      await templateVariablesApi.update(v.id, { active: !v.active });
      await afterWrite();
    } catch (err) {
      handleWriteError(err);
    }
  };

  const remove = async (v: TemplateVariable) => {
    if (v.id == null) return;
    if (
      !window.confirm(
        t(
          'templateVariables.admin.confirmDelete',
          'Delete this variable? Templates that still use it will report it as unknown, and sends that need it will be blocked. To keep the key reserved, turn it off instead.',
        ),
      )
    )
      return;
    setActionError(null);
    try {
      await templateVariablesApi.remove(v.id);
      await afterWrite();
    } catch (err) {
      handleWriteError(err);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t('templateVariables.admin.eyebrow', 'Admin · Communications')}
        title={t('templateVariables.admin.title', 'Template variables')}
        description={t(
          'templateVariables.admin.description',
          'The merge fields your messages, letters and forms can use. System variables come with the platform; add your own for anything else.',
        )}
        actions={
          canEdit ? (
            <Button onClick={() => setEditing('new')}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              {t('templateVariables.admin.new', 'New variable')}
            </Button>
          ) : undefined
        }
      />

      <div className="space-y-4 px-4 pb-8 sm:px-6">
        {!canEdit && (
          <p className="rounded-md border border-v2border bg-paper-2 px-3 py-2 text-sm text-ink-700" data-testid="read-only-notice">
            {t('templateVariables.admin.readOnly', 'You can view these variables. Only organization admins can add or change them.')}
          </p>
        )}
        {actionError && (
          <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
            {actionError}
          </p>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="tv-filter-applies" className="mb-1 block text-xs font-medium text-ink-500">
              {t('templateVariables.admin.filterAppliesTo', 'Used in')}
            </label>
            <select
              id="tv-filter-applies"
              className={inputClass}
              value={appliesFilter}
              onChange={(e) => setAppliesFilter(e.target.value as 'all' | AppliesTo)}
            >
              <option value="all">{t('templateVariables.admin.all', 'All')}</option>
              {APPLIES_TO.map((a) => (
                <option key={a} value={a}>
                  {appliesToLabel(t, a)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="tv-filter-category" className="mb-1 block text-xs font-medium text-ink-500">
              {t('templateVariables.admin.filterCategory', 'Category')}
            </label>
            <select id="tv-filter-category" className={inputClass} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">{t('templateVariables.admin.all', 'All')}</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {categoryLabel(t, c)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="tv-filter-scope" className="mb-1 block text-xs font-medium text-ink-500">
              {t('templateVariables.admin.filterScope', 'Source')}
            </label>
            <select
              id="tv-filter-scope"
              className={inputClass}
              value={scopeFilter}
              onChange={(e) => setScopeFilter(e.target.value as 'all' | TemplateVariable['scope'])}
            >
              <option value="all">{t('templateVariables.admin.all', 'All')}</option>
              <option value="system">{scopeLabel(t, 'system')}</option>
              <option value="org">{scopeLabel(t, 'org')}</option>
              <option value="custom_field">{scopeLabel(t, 'custom_field')}</option>
            </select>
          </div>
          <div className="min-w-[200px] flex-1">
            <label htmlFor="tv-filter-search" className="mb-1 block text-xs font-medium text-ink-500">
              {t('templateVariables.admin.search', 'Search')}
            </label>
            <input
              id="tv-filter-search"
              className={inputClass}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('templateVariables.admin.searchPlaceholder', 'Key, label or description')}
            />
          </div>
        </div>

        {loading && <p className="text-sm text-ink-500">{t('templateVariables.admin.loading', 'Loading…')}</p>}
        {loadError !== null && (
          <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
            {loadError || t('templateVariables.admin.loadError', 'Could not load template variables.')}
          </p>
        )}

        {!loading && loadError === null && (
          <div className="overflow-x-auto rounded-lg border border-v2border bg-surface">
            <table className="w-full text-sm">
              <thead className="border-b border-v2border text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('templateVariables.admin.colVariable', 'Variable')}</th>
                  <th className="px-3 py-2 font-medium">{t('templateVariables.admin.colCategory', 'Category')}</th>
                  <th className="px-3 py-2 font-medium">{t('templateVariables.admin.colSource', 'Value comes from')}</th>
                  <th className="px-3 py-2 font-medium">{t('templateVariables.admin.colAppliesTo', 'Used in')}</th>
                  <th className="px-3 py-2 font-medium">{t('templateVariables.admin.colFallback', 'When empty')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-ink-500">
                      {t('templateVariables.admin.empty', 'No variables match these filters.')}
                    </td>
                  </tr>
                )}
                {shown.map((v) => (
                  <tr key={`${v.scope}:${v.id ?? v.key}`} className={`border-b border-v2border last:border-0 ${v.active ? '' : 'opacity-60'}`}>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-ink">{v.label || v.key}</span>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                            v.scope === 'system'
                              ? 'bg-paper-2 text-ink-700'
                              : v.scope === 'org'
                                ? 'bg-v2accent-soft text-v2accent-ink'
                                : 'bg-paper-2 text-ink-500'
                          }`}
                        >
                          {scopeLabel(t, v.scope)}
                        </span>
                        {!v.active && (
                          <span className="rounded bg-attention-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase text-attention-ink">
                            {t('templateVariables.admin.inactive', 'Off')}
                          </span>
                        )}
                      </div>
                      <code className="font-mono text-xs text-ink-500">{v.placeholder || `{{${v.key}}}`}</code>
                      {v.description && <p className="text-xs text-ink-500">{v.description}</p>}
                    </td>
                    <td className="px-3 py-2 align-top text-ink-700">{categoryLabel(t, v.category)}</td>
                    <td className="px-3 py-2 align-top text-ink-700">
                      {sourceKindLabel(t, v.source_kind)}
                      <span className="block text-xs text-ink-500">{valueTypeLabel(t, v.value_type)}</span>
                      {v.requires && (
                        <span className="block text-xs text-attention-ink">
                          {t('templateVariables.picker.requires', 'Needs {{what}}', { what: v.requires })}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-ink-700">{appliesToLabel(t, v.applies_to)}</td>
                    <td className="px-3 py-2 align-top text-ink-700">
                      {fallbackLabel(t, v.fallback)}
                      {v.fallback === 'default' && v.default_value && (
                        <span className="block text-xs text-ink-500">{`“${v.default_value}”`}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right align-top">
                      {v.scope === 'org' && v.id != null && (
                        <div className="inline-flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setHistoryFor(v)}
                            aria-label={t('templateVariables.admin.history', 'Change history')}
                            title={t('templateVariables.admin.history', 'Change history')}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                          {canEdit && v.editable && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditing(v)}
                                aria-label={t('templateVariables.admin.edit', 'Edit')}
                                title={t('templateVariables.admin.edit', 'Edit')}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => void toggleActive(v)}
                                aria-label={v.active ? t('templateVariables.admin.deactivate', 'Turn off') : t('templateVariables.admin.activate', 'Turn on')}
                                title={v.active ? t('templateVariables.admin.deactivate', 'Turn off') : t('templateVariables.admin.activate', 'Turn on')}
                              >
                                <Power className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => void remove(v)}
                                aria-label={t('templateVariables.admin.delete', 'Delete')}
                                title={t('templateVariables.admin.delete', 'Delete')}
                                className="text-danger"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <VariableEditorDialog
          variable={editing === 'new' ? null : editing}
          customFieldOptions={customFieldOptions}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await afterWrite();
          }}
          onForbidden={() => {
            setForbidden(true);
            setEditing(null);
            setActionError(t('templateVariables.admin.forbidden', 'Only organization admins can change template variables.'));
          }}
        />
      )}
      {historyFor && <VariableHistoryDialog variable={historyFor} onClose={() => setHistoryFor(null)} />}
    </PageContainer>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-1 text-xs text-danger">
      {message}
    </p>
  );
}

function VariableEditorDialog({
  variable,
  customFieldOptions,
  onClose,
  onSaved,
  onForbidden,
}: {
  variable: TemplateVariable | null;
  customFieldOptions: { value: string; label: string }[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  onForbidden: () => void;
}) {
  const { t } = useAppTranslation();
  const isNew = variable === null;
  const [form, setForm] = useState<FormState>(variable ? toForm(variable) : EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => {
      if (!e[key as string] && !e.source_config && !e.form) return e;
      const next = { ...e };
      delete next[key as string];
      delete next.form;
      if (['static_value', 'field', 'field_name', 'prompt', 'options', 'placeholder', 'source_kind', 'value_type'].includes(key as string)) {
        delete next.source_config;
      }
      return next;
    });
  };

  const clientCheck = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (isNew && !KEY_PATTERN.test(form.key.trim())) {
      e.key = t(
        'templateVariables.form.keyFormat',
        'Use lowercase letters, numbers and underscores, starting with a letter (up to 64 characters).',
      );
    }
    if (!form.label.trim()) e.label = t('templateVariables.form.labelRequired', 'Give the variable a label.');
    if (form.source_kind === 'static' && !form.static_value.trim()) {
      e.source_config = t('templateVariables.form.staticRequired', 'Enter the value.');
    }
    if (form.source_kind === 'static' && form.value_type === 'link' && form.static_value.trim() && !/^https?:\/\//i.test(form.static_value.trim())) {
      e.source_config = t('templateVariables.form.linkFormat', 'A link must start with http:// or https://.');
    }
    if ((form.source_kind === 'member_field' || form.source_kind === 'program_field') && !form.field) {
      e.source_config = t('templateVariables.form.fieldRequired', 'Choose a field.');
    }
    if (form.source_kind === 'custom_field' && !form.field_name) {
      e.source_config = t('templateVariables.form.customFieldRequired', 'Choose a custom field.');
    }
    if (form.source_kind === 'input' && form.value_type === 'select' && parseOptions(form.options).length === 0) {
      e.source_config = t('templateVariables.form.optionsRequired', 'List at least one choice.');
    }
    if (form.fallback === 'default' && !form.default_value.trim()) {
      e.default_value = t('templateVariables.form.defaultRequired', 'Enter the default value to use.');
    }
    return e;
  };

  const serverMessage = (e: TemplateVariableApiError): string => {
    switch (e.code) {
      case 'shadows_system_variable':
        return t('templateVariables.form.shadowsSystem', 'A system variable already uses this key. Choose another key.');
      case 'duplicate_key':
        return t('templateVariables.form.duplicateKey', 'Your organization already has a variable with this key.');
      case 'invalid_key':
        return e.message || t('templateVariables.form.keyFormat', 'Use lowercase letters, numbers and underscores, starting with a letter (up to 64 characters).');
      default:
        return e.message || t('templateVariables.admin.actionFailed', 'That change could not be saved.');
    }
  };

  const save = async () => {
    const local = clientCheck();
    if (Object.keys(local).length > 0) {
      setErrors(local);
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      const body = buildBody(form, isNew);
      if (isNew) await templateVariablesApi.create(body);
      else await templateVariablesApi.update(variable!.id as number, body);
      await onSaved();
    } catch (err) {
      const e = parseApiError(err);
      if (e.status === 403) {
        onForbidden();
        return;
      }
      const field = errorField(e);
      setErrors({ [field]: serverMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const valueInputType =
    form.value_type === 'date' ? 'date' : form.value_type === 'datetime' ? 'datetime-local' : form.value_type === 'number' ? 'number' : form.value_type === 'link' ? 'url' : 'text';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNew ? t('templateVariables.form.createTitle', 'New template variable') : t('templateVariables.form.editTitle', 'Edit template variable')}
          </DialogTitle>
          <DialogDescription>
            {t('templateVariables.form.intro', 'Staff insert it in a template as {{token}}.', {
              token: `{{${form.key || 'key'}}}`,
            })}
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
          noValidate
        >
          {errors.form && (
            <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
              {errors.form}
            </p>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="tv-key" className="mb-1 block text-xs font-medium text-ink-700">
                {t('templateVariables.form.key', 'Key')}
              </label>
              <input
                id="tv-key"
                className={`${inputClass} font-mono`}
                value={form.key}
                disabled={!isNew}
                onChange={(e) => set('key', e.target.value)}
                placeholder="clinic_hours"
                aria-invalid={!!errors.key}
              />
              {isNew && !errors.key && (
                <p className="mt-1 text-[11px] text-ink-500">
                  {t('templateVariables.form.keyHelp', 'Lowercase, numbers and underscores. It cannot be changed later.')}
                </p>
              )}
              <FieldError message={errors.key} />
            </div>
            <div>
              <label htmlFor="tv-label" className="mb-1 block text-xs font-medium text-ink-700">
                {t('templateVariables.form.label', 'Label')}
              </label>
              <input id="tv-label" className={inputClass} value={form.label} onChange={(e) => set('label', e.target.value)} aria-invalid={!!errors.label} />
              <FieldError message={errors.label} />
            </div>
          </div>

          <div>
            <label htmlFor="tv-description" className="mb-1 block text-xs font-medium text-ink-700">
              {t('templateVariables.form.description', 'Description')}
            </label>
            <input id="tv-description" className={inputClass} value={form.description} onChange={(e) => set('description', e.target.value)} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="tv-category" className="mb-1 block text-xs font-medium text-ink-700">
                {t('templateVariables.form.category', 'Category')}
              </label>
              <input id="tv-category" className={inputClass} value={form.category} onChange={(e) => set('category', e.target.value)} />
            </div>
            <div>
              <label htmlFor="tv-value-type" className="mb-1 block text-xs font-medium text-ink-700">
                {t('templateVariables.form.valueType', 'Type of value')}
              </label>
              <select id="tv-value-type" className={inputClass} value={form.value_type} onChange={(e) => set('value_type', e.target.value as ValueType)}>
                {VALUE_TYPES.map((v) => (
                  <option key={v} value={v}>
                    {valueTypeLabel(t, v)}
                  </option>
                ))}
              </select>
              <FieldError message={errors.value_type} />
            </div>
            <div>
              <label htmlFor="tv-applies-to" className="mb-1 block text-xs font-medium text-ink-700">
                {t('templateVariables.form.appliesTo', 'Used in')}
              </label>
              <select id="tv-applies-to" className={inputClass} value={form.applies_to} onChange={(e) => set('applies_to', e.target.value as AppliesTo)}>
                {APPLIES_TO.map((a) => (
                  <option key={a} value={a}>
                    {appliesToLabel(t, a)}
                  </option>
                ))}
              </select>
              <FieldError message={errors.applies_to} />
            </div>
          </div>

          <div className="space-y-3 rounded-md border border-v2border p-3">
            <div>
              <label htmlFor="tv-source-kind" className="mb-1 block text-xs font-medium text-ink-700">
                {t('templateVariables.form.sourceKind', 'Where the value comes from')}
              </label>
              <select
                id="tv-source-kind"
                className={inputClass}
                value={form.source_kind}
                onChange={(e) => set('source_kind', e.target.value as EditableKind)}
              >
                {SOURCE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {sourceKindLabel(t, k)}
                  </option>
                ))}
              </select>
              <FieldError message={errors.source_kind} />
            </div>

            {form.source_kind === 'static' && (
              <div>
                <label htmlFor="tv-static-value" className="mb-1 block text-xs font-medium text-ink-700">
                  {t('templateVariables.form.staticValue', 'Value')}
                </label>
                {form.value_type === 'boolean' ? (
                  <select id="tv-static-value" className={inputClass} value={form.static_value} onChange={(e) => set('static_value', e.target.value)}>
                    <option value="">{t('templateVariables.inputs.choose', 'Choose…')}</option>
                    <option value="yes">{t('templateVariables.inputs.yes', 'Yes')}</option>
                    <option value="no">{t('templateVariables.inputs.no', 'No')}</option>
                  </select>
                ) : (
                  <input
                    id="tv-static-value"
                    type={valueInputType}
                    className={inputClass}
                    value={form.static_value}
                    onChange={(e) => set('static_value', e.target.value)}
                    placeholder={form.value_type === 'link' ? 'https://' : undefined}
                  />
                )}
              </div>
            )}

            {form.source_kind === 'member_field' && (
              <div>
                <label htmlFor="tv-member-field" className="mb-1 block text-xs font-medium text-ink-700">
                  {t('templateVariables.form.memberField', 'Member field')}
                </label>
                <select id="tv-member-field" className={inputClass} value={form.field} onChange={(e) => set('field', e.target.value)}>
                  <option value="">{t('templateVariables.inputs.choose', 'Choose…')}</option>
                  {MEMBER_FIELDS.map((f) => (
                    <option key={f} value={f}>
                      {memberFieldLabel(t, f)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {form.source_kind === 'program_field' && (
              <div>
                <label htmlFor="tv-program-field" className="mb-1 block text-xs font-medium text-ink-700">
                  {t('templateVariables.form.programField', 'Program field')}
                </label>
                <select id="tv-program-field" className={inputClass} value={form.field} onChange={(e) => set('field', e.target.value)}>
                  <option value="">{t('templateVariables.inputs.choose', 'Choose…')}</option>
                  {PROGRAM_FIELDS.map((f) => (
                    <option key={f} value={f}>
                      {programFieldLabel(t, f)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-ink-500">
                  {t('templateVariables.form.programFieldHelp', 'Filled from the program the send or form is linked to.')}
                </p>
              </div>
            )}

            {form.source_kind === 'custom_field' && (
              <div>
                <label htmlFor="tv-custom-field" className="mb-1 block text-xs font-medium text-ink-700">
                  {t('templateVariables.form.customField', 'Custom field')}
                </label>
                <select id="tv-custom-field" className={inputClass} value={form.field_name} onChange={(e) => set('field_name', e.target.value)}>
                  <option value="">{t('templateVariables.inputs.choose', 'Choose…')}</option>
                  {customFieldOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {form.source_kind === 'input' && (
              <div className="space-y-3">
                <p className="text-[11px] text-ink-500">
                  {t('templateVariables.form.inputHelp', 'Staff type this value when they send the message or start the form.')}
                </p>
                <div>
                  <label htmlFor="tv-prompt" className="mb-1 block text-xs font-medium text-ink-700">
                    {t('templateVariables.form.prompt', 'Question shown to staff')}
                  </label>
                  <input id="tv-prompt" className={inputClass} value={form.prompt} onChange={(e) => set('prompt', e.target.value)} />
                </div>
                {form.value_type === 'select' ? (
                  <div>
                    <label htmlFor="tv-options" className="mb-1 block text-xs font-medium text-ink-700">
                      {t('templateVariables.form.options', 'Choices (one per line)')}
                    </label>
                    <textarea id="tv-options" rows={3} className={inputClass} value={form.options} onChange={(e) => set('options', e.target.value)} />
                  </div>
                ) : (
                  <div>
                    <label htmlFor="tv-placeholder" className="mb-1 block text-xs font-medium text-ink-700">
                      {t('templateVariables.form.placeholder', 'Hint inside the box')}
                    </label>
                    <input id="tv-placeholder" className={inputClass} value={form.placeholder} onChange={(e) => set('placeholder', e.target.value)} />
                  </div>
                )}
              </div>
            )}
            <FieldError message={errors.source_config} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="tv-fallback" className="mb-1 block text-xs font-medium text-ink-700">
                {t('templateVariables.form.fallback', 'When there is no value')}
              </label>
              <select id="tv-fallback" className={inputClass} value={form.fallback} onChange={(e) => set('fallback', e.target.value as Fallback)}>
                {FALLBACKS.map((f) => (
                  <option key={f} value={f}>
                    {fallbackLabel(t, f)}
                  </option>
                ))}
              </select>
              <FieldError message={errors.fallback} />
            </div>
            <div>
              <label htmlFor="tv-default" className="mb-1 block text-xs font-medium text-ink-700">
                {t('templateVariables.form.defaultValue', 'Default value')}
              </label>
              <input
                id="tv-default"
                className={inputClass}
                value={form.default_value}
                onChange={(e) => set('default_value', e.target.value)}
                disabled={form.fallback !== 'default' && form.source_kind !== 'input'}
                aria-invalid={!!errors.default_value}
              />
              <FieldError message={errors.default_value} />
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
            {t('templateVariables.form.active', 'On (available in templates)')}
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('templateVariables.form.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              <Braces className="mr-1.5 h-4 w-4" aria-hidden />
              {isNew ? t('templateVariables.form.create', 'Create variable') : t('templateVariables.form.save', 'Save changes')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function VariableHistoryDialog({ variable, onClose }: { variable: TemplateVariable; onClose: () => void }) {
  const { t } = useAppTranslation();
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (variable.id == null) return;
    templateVariablesApi
      .history(variable.id)
      .then(setEntries)
      .catch((err) => setError(parseApiError(err).message || t('templateVariables.history.loadError', 'Could not load the history.')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variable.id]);

  const actionLabel = (a: string) =>
    a === 'created'
      ? t('templateVariables.history.created', 'Created')
      : a === 'updated'
        ? t('templateVariables.history.updated', 'Changed')
        : a === 'deleted'
          ? t('templateVariables.history.deleted', 'Deleted')
          : a;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('templateVariables.history.title', 'Change history')}</DialogTitle>
          <DialogDescription>
            <code className="font-mono">{`{{${variable.key}}}`}</code>
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        {!entries && !error && <p className="text-sm text-ink-500">{t('templateVariables.admin.loading', 'Loading…')}</p>}
        {entries && entries.length === 0 && <p className="text-sm text-ink-500">{t('templateVariables.history.empty', 'No changes recorded yet.')}</p>}
        {entries && entries.length > 0 && (
          <ol className="space-y-3" data-testid="variable-history">
            {entries.map((h) => (
              <li key={h.id} className="rounded-md border border-v2border p-3 text-sm">
                <p className="font-medium text-ink">
                  {actionLabel(h.action)}
                  {h.version_number != null && <span className="ml-2 text-xs text-ink-500">{`v${h.version_number}`}</span>}
                </p>
                <p className="text-xs text-ink-500">
                  {[h.performed_by_name, h.performed_at ? new Date(h.performed_at).toLocaleString() : null].filter(Boolean).join(' · ')}
                </p>
                {h.field_changes && Object.keys(h.field_changes).length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {Object.entries(h.field_changes).map(([field, change]) => (
                      <li key={field} className="text-xs text-ink-700">
                        <code className="font-mono">{field}</code>: {formatValue(change?.old)} → {formatValue(change?.new)}
                      </li>
                    ))}
                  </ul>
                )}
                {h.notes && <p className="mt-1 text-xs text-ink-500">{h.notes}</p>}
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
